import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { WorkerLayout } from '../../components/layouts/WorkerLayout';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MapPin, Clock, AlertTriangle, ChevronRight, Calendar, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const WorkerDashboard = () => {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayJobs, setTodayJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [statsRes, jobsRes, notifRes] = await Promise.all([
        api.get('/dashboard/worker-stats'),
        api.get('/jobs', { params: { date_from: today, date_to: today } }),
        api.get('/notifications')
      ]);
      setStats(statsRes.data);
      setTodayJobs(jobsRes.data);
      setNotifications(notifRes.data.filter(n => !n.read).slice(0, 3));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) return <WorkerLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div></div></WorkerLayout>;

  return (
    <WorkerLayout>
      <div className="p-4 space-y-6" data-testid="worker-dashboard">
        {/* Header */}
        <div className="pt-2">
          <p className="text-slate-500">{format(new Date(), "EEEE, d 'de' MMMM", { locale: es })}</p>
          <h1 className="font-display text-2xl font-bold text-slate-900">Hola, {user?.name?.split(' ')[0]}</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-sky-600">{stats?.jobs_today || 0}</p><p className="text-sm text-slate-500">Trabajos hoy</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-green-600">{stats?.completed_today || 0}</p><p className="text-sm text-slate-500">Completados</p></CardContent></Card>
        </div>

        {/* Notifications */}
        {notifications.length > 0 && (
          <div className="space-y-2">
            <h2 className="font-semibold text-slate-900">Notificaciones</h2>
            {notifications.map(notif => (
              <motion.div key={notif.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className={`p-4 rounded-lg ${notif.type === 'emergency' ? 'bg-red-50 border border-red-200' : 'bg-sky-50 border border-sky-200'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {notif.type === 'emergency' && <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />}
                    <div><h4 className="font-medium">{notif.title}</h4><p className="text-sm text-slate-600">{notif.message}</p></div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => markNotificationRead(notif.id)}>OK</Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Today's Jobs */}
        <div className="space-y-3">
          <h2 className="font-semibold text-slate-900">Trabajos de hoy</h2>
          {todayJobs.length === 0 ? (
            <Card><CardContent className="p-8 text-center"><Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p className="text-slate-500">No tienes trabajos programados para hoy</p></CardContent></Card>
          ) : (
            todayJobs.map((job, index) => (
              <motion.div key={job.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                <Link to={`/worker/job/${job.id}`}>
                  <Card className={`hover:shadow-md transition-shadow ${job.is_emergency ? 'border-red-300 bg-red-50' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{job.title}</h3>
                            {job.is_emergency && <Badge className="bg-red-500 text-white text-xs">URGENTE</Badge>}
                            {job.status === 'completed' && <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" />Completado</Badge>}
                          </div>
                          <p className="text-sm font-medium text-slate-700">{job.client_name}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{job.client_address}</span>
                            {job.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{job.scheduled_time}</span>}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </WorkerLayout>
  );
};

export default WorkerDashboard;
